import type { Priority } from '@/shared/lib/priority';
import type { BoardViewType } from '@/features/tasks/public';
import type { StatusType } from '@/features/boards/public';
import type { FieldType } from '@/features/custom-fields/public';

export const TEMPLATE_SECTOR = {
  Technology: 'Technology',
  Marketing: 'Marketing',
  Industrial: 'Industrial',
  ProfessionalServices: 'ProfessionalServices',
} as const;

export type TemplateSector = (typeof TEMPLATE_SECTOR)[keyof typeof TEMPLATE_SECTOR];

export interface TemplateTask {
  title: string;
  /** `undefined` round-trips as the backend's `"None"` priority. */
  priority: Priority | undefined;
}

export interface TemplateGroup {
  name: string;
  order: number;
  tasks: TemplateTask[];
}

export interface TemplateStatus {
  name: string;
  color: string;
  type: StatusType;
  order: number;
}

export interface TemplateView {
  type: BoardViewType;
  name: string;
  /** Decorative only — never persisted by the backend on instantiate. */
  isDefault: boolean;
}

export interface TemplateCustomField {
  name: string;
  type: FieldType;
  /** Required non-empty for Select/MultiSelect, `null` otherwise. */
  options: string[] | null;
}

export interface TemplateTag {
  name: string;
  color: string;
}

/** One board's structure inside a template — a template can define more than one. */
export interface TemplateBoard {
  name: string;
  description: string | null;
  order: number;
  statuses: TemplateStatus[];
  views: TemplateView[];
  groups: TemplateGroup[];
  /** Tasks with no group — a sibling of `groups`, not a synthetic group. */
  ungroupedTasks: TemplateTask[];
}

/** Thin shape returned by the list endpoint — no structural content or counts. */
export interface ProjectTemplateSummary {
  id: string;
  tenantId: string | null;
  title: string;
  sector: TemplateSector;
  isSystem: boolean;
  updatedAt: string;
}

/** Full shape used by the preview panel and editors — `structureJson` already decoded. */
export interface ProjectTemplate {
  id: string;
  tenantId: string | null;
  title: string;
  sector: TemplateSector;
  description: string | null;
  isSystem: boolean;
  boards: TemplateBoard[];
  customFields: TemplateCustomField[];
  tags: TemplateTag[];
  createdById: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * `ProjectTemplateSummary` enriched with fields the gallery card needs (`description`,
 * `taskCount`, `statusCount`) that the backend's summary endpoint doesn't return — derived
 * client-side by fetching each template's detail. Temporary until backend adds them to
 * `ProjectTemplateSummaryDto` (see `tasks/project-templates-backend-contract.md` §7).
 */
export interface TemplateGalleryItem extends ProjectTemplateSummary {
  description: string | null;
  taskCount: number;
  statusCount: number;
}

export interface ProjectTemplateFilters {
  sector?: TemplateSector;
  scope?: 'system' | 'tenant';
}

export interface CreateProjectTemplatePayload {
  title: string;
  sector: TemplateSector;
  description?: string;
  boards: TemplateBoard[];
  customFields: TemplateCustomField[];
  tags: TemplateTag[];
}

export interface UpdateTemplateMetadataPayload {
  title: string;
  sector: TemplateSector;
  description?: string;
}

export interface UpdateTemplateStructurePayload {
  boards: TemplateBoard[];
  customFields: TemplateCustomField[];
  tags: TemplateTag[];
}

export interface SaveAsTemplatePayload {
  title: string;
  sector: TemplateSector;
  description?: string;
}

/** `{ id }` of the project created by the instantiate endpoint. */
export interface InstantiateTemplateResult {
  id: string;
}
